import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState, NoDataIcon } from '@/components/ui/EmptyState';
import { useChains } from '@/hooks/queries/useChains';
import { formatExpirationDate, getDaysToExpiration, formatCurrency } from '@/lib/formatters';
import { cn, groupBy } from '@/lib/utils';

export function PositionsByExpiration() {
  const navigate = useNavigate();
  const { data: chains, isLoading } = useChains({ status: 'open' });

  const positionsByExpiration = useMemo(() => {
    if (!chains) return [];

    const allLegs: Array<{
      chainId: string;
      symbol: string;
      expiration: Date;
      dte: number;
      premium: number;
    }> = [];

    for (const chain of chains) {
      const chainPositions = chain.positions || [];
      for (const position of chainPositions) {
        if (position.status !== 'open') continue;
        const positionLegs = position.legs || [];
        for (const leg of positionLegs) {
          const expDate = new Date(leg.expirationDate);
          allLegs.push({
            chainId: chain.id,
            symbol: chain.underlyingSymbol,
            expiration: expDate,
            dte: getDaysToExpiration(expDate),
            premium: position.netPremium,
          });
        }
      }
    }

    // Group by expiration date
    const grouped = groupBy(allLegs, (leg) => leg.expiration.toISOString().split('T')[0]);

    // Convert to sorted array
    return Object.entries(grouped)
      .map(([date, legs]) => ({
        date,
        dte: legs[0].dte,
        count: legs.length,
        symbols: [...new Set(legs.map((l) => l.symbol))],
        chainIds: [...new Set(legs.map((l) => l.chainId))],
        totalPremium: legs.reduce((sum, l) => sum + l.premium, 0),
      }))
      .filter((g) => g.dte >= 0)
      .sort((a, b) => a.dte - b.dte)
      .slice(0, 5);
  }, [chains]);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Upcoming Expirations</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center py-8">
          <Spinner />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Upcoming Expirations</CardTitle>
      </CardHeader>
      <CardContent>
        {positionsByExpiration.length === 0 ? (
          <EmptyState
            icon={<NoDataIcon />}
            title="No upcoming expirations"
            description="Open some positions to see them here"
          />
        ) : (
          <div className="space-y-3">
            {positionsByExpiration.map((group) => (
              <div
                key={group.date}
                className={cn(
                  'p-3 rounded-lg border cursor-pointer transition-colors',
                  group.dte <= 7
                    ? 'border-yellow-200 bg-yellow-50 hover:bg-yellow-100'
                    : 'border-gray-200 bg-white hover:bg-gray-50'
                )}
                onClick={() => {
                  if (group.chainIds.length === 1) {
                    navigate(`/chains/${group.chainIds[0]}`);
                  } else {
                    navigate(`/chains?status=open&symbol=${group.symbols[0]}`);
                  }
                }}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">
                      {formatExpirationDate(group.date)}
                    </span>
                    <span
                      className={cn(
                        'text-xs px-2 py-0.5 rounded-full',
                        group.dte <= 7
                          ? 'bg-yellow-200 text-yellow-700'
                          : 'bg-gray-100 text-gray-600'
                      )}
                    >
                      {group.dte === 0 ? 'Today' : `${group.dte}d`}
                    </span>
                  </div>
                  <span className="text-sm text-gray-500">
                    {group.count} leg{group.count !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">{group.symbols.join(', ')}</span>
                  <span className="font-medium text-gray-900">
                    {formatCurrency(group.totalPremium)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
